import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { validateMaterial, validateApplication } from "@/lib/validationSchemas";

export type PendingReview = Tables<"pending_reviews">;

export const usePendingReviews = () => {
  return useQuery({
    queryKey: ["pending-reviews"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pending_reviews")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as PendingReview[];
    },
  });
};

export const useApproveReview = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (review: PendingReview) => {
      const rawData = review.raw_data as Record<string, unknown>;
      
      if (review.item_type === "material") {
        // Validate material data before insertion
        const validationResult = validateMaterial(rawData);
        
        if (validationResult.success === false) {
          throw new Error(`Validation failed: ${validationResult.errors.join(", ")}`);
        }
        
        const validatedData = validationResult.data;
        
        // Insert into materials table with validated data
        const { error: insertError } = await supabase
          .from("materials")
          .insert({
            id: review.item_id,
            name: validatedData.name,
            name_cn: validatedData.name_cn ?? null,
            family: validatedData.family,
            description: validatedData.description ?? null,
            source_paper: validatedData.source_paper ?? null,
            source_doi: validatedData.source_doi ?? null,
            source_url: validatedData.source_url ?? null,
            paper_abstract: validatedData.paper_abstract ?? null,
            degradation_value: validatedData.degradation_value ?? null,
            degradation_source: validatedData.degradation_source ?? null,
            degradation_method: validatedData.degradation_method ?? null,
            tensile_strength_mpa: validatedData.tensile_strength_mpa ?? null,
            tensile_source: validatedData.tensile_source ?? null,
            electrical_performance: validatedData.electrical_performance ?? null,
            electrical_source: validatedData.electrical_source ?? null,
            water_resistance: validatedData.water_resistance ?? null,
            water_resistance_source: validatedData.water_resistance_source ?? null,
            cost_per_kg: validatedData.cost_per_kg ?? null,
            cost_source: validatedData.cost_source ?? null,
            applications_mentioned: validatedData.applications_mentioned ?? null,
            applications_source: validatedData.applications_source ?? null,
            fabrication_method: validatedData.fabrication_method ?? null,
            fabrication_source: validatedData.fabrication_source ?? null,
            image_url: validatedData.image_url ?? null,
            image_credit: validatedData.image_credit ?? null,
            image_source_url: validatedData.image_source_url ?? null,
            published: true,
            status: "approved",
          });

        if (insertError) throw insertError;
      } else if (review.item_type === "application") {
        // Validate application data before insertion
        const validationResult = validateApplication(rawData);
        
        if (validationResult.success === false) {
          throw new Error(`Validation failed: ${validationResult.errors.join(", ")}`);
        }
        
        const validatedData = validationResult.data;
        
        const { error: insertError } = await supabase
          .from("applications")
          .insert({
            id: review.item_id,
            title: validatedData.title,
            title_cn: validatedData.title_cn ?? null,
            description: validatedData.description ?? null,
            category: validatedData.category ?? null,
            application_type: validatedData.application_type ?? null,
            image_url: validatedData.image_url ?? null,
            image_credit: validatedData.image_credit ?? null,
            image_source_url: validatedData.image_source_url ?? null,
            source_url: validatedData.source_url ?? null,
            year: validatedData.year ?? null,
            creators: validatedData.creators ?? null,
            organization: validatedData.organization ?? null,
            technologies: validatedData.technologies ?? null,
            key_features: validatedData.key_features ?? null,
            technical_brief: validatedData.technical_brief ?? null,
            impact_brief: validatedData.impact_brief ?? null,
            trl_level: validatedData.trl_level ?? null,
            tags: validatedData.tags ?? null,
            status: "approved",
          });

        if (insertError) throw insertError;
      }

      // Delete from pending_reviews
      const { error: deleteError } = await supabase
        .from("pending_reviews")
        .delete()
        .eq("id", review.id);

      if (deleteError) throw deleteError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pending-reviews"] });
      queryClient.invalidateQueries({ queryKey: ["materials"] });
      queryClient.invalidateQueries({ queryKey: ["applications"] });
    },
  });
};

export const useRejectReview = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (reviewId: string) => {
      const { error } = await supabase
        .from("pending_reviews")
        .delete()
        .eq("id", reviewId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pending-reviews"] });
    },
  });
};

export const useUpdatePendingReview = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, rawData }: { id: string; rawData: Record<string, unknown> }) => {
      const { error } = await supabase
        .from("pending_reviews")
        .update({ raw_data: rawData as unknown as Tables<"pending_reviews">["raw_data"] })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pending-reviews"] });
    },
  });
};
